import { Typography } from '@mui/material';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { GeoEventBaseChangelog } from 'features/geoEvents/components/GeoEventChangelog';
import { useGeoEventGeolocationFormFieldChanges } from 'features/geoEvents/hooks/useGeoEventGeolocationFormFieldChanges';
import { Flooding } from 'features/flooding/flooding.types';
import { EvacZone, RegionEvacZoneStyle } from 'shared/types';
import { useNotificationTypeFormFieldChanges } from 'features/geoEvents/hooks/useNotificationTypeFormFieldChanges';
import { useFloodingParentFormFieldChanges } from 'features/flooding/hooks/useFloodingParentFormFieldChanges';

type FloodingChangelogProps = {
  flooding: Flooding;
  evacZones: EvacZone[];
  zoneStyle: RegionEvacZoneStyle;
};

type FloodingFormValues = {
  areaImpacted: string;
};

export const FloodingChangelog = (
  props: FloodingChangelogProps,
): JSX.Element | null => {
  const { flooding, evacZones, zoneStyle } = props;
  const { t } = useTranslation();

  const locationChanges = useGeoEventGeolocationFormFieldChanges({
    geoEvent: flooding,
  });
  const notificationTypeChanges = useNotificationTypeFormFieldChanges({
    geoEvent: flooding,
  });
  const parentChanges = useFloodingParentFormFieldChanges({
    flooding,
  });

  const areaImpacted = useWatch<FloodingFormValues>({
    name: 'areaImpacted',
  });

  const areaImpactedChanged =
    (flooding.data.areaImpacted ?? '') !== (areaImpacted ?? '');

  return (
    <GeoEventBaseChangelog
      geoEvent={flooding}
      evacZones={evacZones}
      zoneStyle={zoneStyle}
    >
      {Object.values(notificationTypeChanges).map((change) => (
        <Typography key={change}>{change}</Typography>
      ))}

      {Object.values(locationChanges).map((change) => (
        <Typography key={change}>{change}</Typography>
      ))}

      {areaImpactedChanged && (
        <Typography>
          {t('geoEventChangelog.areaImpacted', {
            prevValue: flooding.data.areaImpacted || t('common.none'),
            newValue: areaImpacted || t('common.none'),
          })}
        </Typography>
      )}

      {Object.values(parentChanges).map((change) => (
        <Typography key={change}>{change}</Typography>
      ))}
    </GeoEventBaseChangelog>
  );
};
